import { S } from '@/shared/constants/styles';
import { FREQUENCIES, CUSTOM_SCHEDULE_TYPES, WEEKDAY_NAMES, NTH_LABELS } from '@/shared/constants/config';
import FormField from '@/shared/components/FormField';
import CategoryPicker from '@/features/categories/components/CategoryPicker';
import { useCategoriesContext } from '@/app/providers/CategoriesProvider';
import { useSettingsContext } from '@/app/providers/SettingsProvider';

export default function BillForm({ billForm, setBillForm, saveBill, onClose, errors = {}, setErrors, editing = false }) {
  const { allSubCats, getGroup } = useCategoriesContext();
  const { t, isDark } = useSettingsContext();

  const handleSave = async () => {
    const nextErrors = {};
    if (!billForm.name?.trim()) nextErrors.name = 'Name is required';
    if (!billForm.amount || isNaN(parseFloat(billForm.amount))) nextErrors.amount = 'Enter a valid amount';
    if (!billForm.category) nextErrors.category = 'Pick a category';
    if (billForm.frequency === 'Custom') {
      const s = billForm.customSchedule || {};
      if (s.type === CUSTOM_SCHEDULE_TYPES.EVERY_N_WEEKS) {
        const weeks = Number(s.intervalWeeks);
        if (!weeks || weeks < 1) nextErrors.customSchedule = 'Interval must be at least 1 week';
      }
      if (s.type === CUSTOM_SCHEDULE_TYPES.NTH_WEEKDAY) {
        if (s.nth === undefined || s.nth === null || s.nth === '') nextErrors.customSchedule = 'Pick which week of the month';
      }
    }
    if (setErrors) setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const success = await saveBill();
    if (success && onClose) onClose();
  };

  const schedule = billForm.customSchedule || {};
  const setSchedule = (patch) => setBillForm({ ...billForm, customSchedule: { ...schedule, ...patch } });
  const isCustom = billForm.frequency === 'Custom';
  const isNthWeekday = schedule.type === CUSTOM_SCHEDULE_TYPES.NTH_WEEKDAY;
  const isEveryNWeeks = schedule.type === CUSTOM_SCHEDULE_TYPES.EVERY_N_WEEKS;

  return (
    <>
      <FormField label="Service / Bill Name" error={errors.name}>
        <input value={billForm.name} onChange={e => setBillForm({ ...billForm, name: e.target.value })} placeholder="e.g. Electricity" style={S.input} />
      </FormField>
      <FormField label="Amount (AUD)" error={errors.amount}>
        <input type="number" step="0.01" value={billForm.amount} onChange={e => setBillForm({ ...billForm, amount: e.target.value })} style={S.input} />
      </FormField>
      <FormField label="Category" error={errors.category}>
        <CategoryPicker
          value={billForm.category || ''}
          onChange={(c) => setBillForm({ ...billForm, category: c })}
          options={allSubCats}
          getGroup={getGroup}
          theme={t}
          isDark={isDark}
          placeholder="Pick a category"
        />
      </FormField>
      <FormField label="Frequency">
        <select value={billForm.frequency} onChange={e => setBillForm({ ...billForm, frequency: e.target.value })} style={S.select}>
          {FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </FormField>
      {!isCustom && (
        <FormField label="First due date">
          <input
            type="date"
            value={billForm.dueDate || ''}
            onChange={e => setBillForm({ ...billForm, dueDate: e.target.value })}
            style={S.input}
          />
          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>
            Frequency rolls this date forward automatically.
          </div>
        </FormField>
      )}
      {isCustom && (
        <div style={{ background: t.tableBg, border: '1px solid ' + t.cardBorder, borderRadius: 10, padding: 12, marginTop: 4 }}>
          <FormField label="Custom schedule type" error={errors.customSchedule}>
            <select
              value={schedule.type || CUSTOM_SCHEDULE_TYPES.EVERY_N_WEEKS}
              onChange={e => setSchedule({ type: e.target.value })}
              style={S.select}
            >
              <option value={CUSTOM_SCHEDULE_TYPES.EVERY_N_WEEKS}>Every N weeks on a weekday</option>
              <option value={CUSTOM_SCHEDULE_TYPES.NTH_WEEKDAY}>Nth weekday of the month</option>
            </select>
          </FormField>
          {isEveryNWeeks && (
            <>
              <FormField label="Every (weeks)">
                <input
                  type="number"
                  min="1"
                  max="52"
                  value={schedule.intervalWeeks ?? 2}
                  onChange={e => setSchedule({ intervalWeeks: Number(e.target.value) })}
                  style={S.input}
                />
              </FormField>
              <FormField label="On">
                <select value={schedule.dayOfWeek ?? 4} onChange={e => setSchedule({ dayOfWeek: Number(e.target.value) })} style={S.select}>
                  {WEEKDAY_NAMES.map((name, i) => <option key={name} value={i}>{name}</option>)}
                </select>
              </FormField>
              <FormField label="Starting from (anchor date)">
                <input
                  type="date"
                  value={schedule.anchorDate || ''}
                  onChange={e => setSchedule({ anchorDate: e.target.value })}
                  style={S.input}
                />
                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>Leave blank to anchor on today.</div>
              </FormField>
            </>
          )}
          {isNthWeekday && (
            <>
              <FormField label="Which week">
                <select value={String(schedule.nth ?? 2)} onChange={e => setSchedule({ nth: Number(e.target.value) })} style={S.select}>
                  {[1, 2, 3, 4, -1].map(n => <option key={n} value={n}>{NTH_LABELS[String(n)]}</option>)}
                </select>
              </FormField>
              <FormField label="Weekday">
                <select value={schedule.dayOfWeek ?? 4} onChange={e => setSchedule({ dayOfWeek: Number(e.target.value) })} style={S.select}>
                  {WEEKDAY_NAMES.map((name, i) => <option key={name} value={i}>{name}</option>)}
                </select>
              </FormField>
            </>
          )}
        </div>
      )}
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: t.textSub }}>
        <input type="checkbox" checked={!!billForm.autoPay} onChange={e => setBillForm({ ...billForm, autoPay: e.target.checked })} />
        Auto-pay enabled
      </label>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: t.textSub, marginTop: 8 }}>
        <input type="checkbox" checked={billForm.autoGenerate !== false} onChange={e => setBillForm({ ...billForm, autoGenerate: e.target.checked })} />
        Auto-create transactions on schedule
      </label>
      <button onClick={handleSave} style={{ ...S.btn(), width: "100%", padding: 13, borderRadius: 10, fontSize: 14, marginTop: 12 }}>{editing ? 'Save Changes' : 'Add Bill'}</button>
    </>
  );
}
